import React, { useState, useEffect } from 'react';
import { 
  getAllApplications, 
  getApplicationsByType, 
  updateApplicationStatus,
  listenToApplications 
} from '../firebase';
import './ApplicationDashboard.css';

const ApplicationDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');

  const applicationTypes = ['All', 'Mentor', 'Mentee', 'Business Partner'];
  const statusTypes = ['All', 'pending', 'approved', 'rejected'];

  useEffect(() => {
    // Set up real-time listener for applications
    const unsubscribe = listenToApplications((apps) => {
      // Sort by creation date (newest first)
      const sortedApps = apps.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
      
      setApplications(sortedApps);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Filter applications based on selected type and status
    let filtered = applications;

    if (selectedType !== 'All') {
      filtered = filtered.filter(app => app.applicationType === selectedType);
    }

    if (selectedStatus !== 'All') {
      filtered = filtered.filter(app => app.status === selectedStatus);
    }

    setFilteredApplications(filtered);
  }, [applications, selectedType, selectedStatus]);

  const handleStatusUpdate = async (applicationId, newStatus) => {
    const result = await updateApplicationStatus(applicationId, newStatus);
    if (result.error) {
      alert(`Error updating status: ${result.error}`);
    }
    // Real-time listener will automatically update the UI
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Application Dashboard</h1>
        <div className="dashboard-stats">
          Total Applications: {applications.length}
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-filters">
        <div className="filter-group">
          <label className="filter-label">Type:</label>
          <select 
            value={selectedType} 
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            {applicationTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Status:</label>
          <select 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            {statusTypes.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Applications List */}
      <div className="dashboard-applications">
        {filteredApplications.length === 0 ? (
          <div className="dashboard-empty">
            No applications found matching the selected filters.
          </div>
        ) : (
          filteredApplications.map((app) => (
            <div key={app.id} className="application-card">
              <div className="application-header">
                <div className="application-type-badge">
                  {app.applicationType}
                </div>
                <div className={`application-status status-${app.status}`}>
                  {app.status}
                </div>
              </div>

              <div className="application-content">
                <div className="application-field">
                  <strong>Name:</strong> {app.name}
                </div>
                <div className="application-field">
                  <strong>Email:</strong> {app.email}
                </div>
                {app.websiteLink && (
                  <div className="application-field">
                    <strong>Website:</strong> 
                    <a href={app.websiteLink} target="_blank" rel="noopener noreferrer">
                      {app.websiteLink}
                    </a>
                  </div>
                )}
                {app.message && (
                  <div className="application-field">
                    <strong>Message:</strong>
                    <div className="application-message">{app.message}</div>
                  </div>
                )}
                <div className="application-field">
                  <strong>Applied:</strong> {formatDate(app.createdAt)}
                </div>
              </div>

              <div className="application-actions">
                <button 
                  onClick={() => handleStatusUpdate(app.id, 'approved')}
                  className="action-btn approve-btn"
                  disabled={app.status === 'approved'}
                >
                  Approve
                </button>
                <button 
                  onClick={() => handleStatusUpdate(app.id, 'rejected')}
                  className="action-btn reject-btn"
                  disabled={app.status === 'rejected'}
                >
                  Reject
                </button>
                <button 
                  onClick={() => handleStatusUpdate(app.id, 'pending')}
                  className="action-btn pending-btn"
                  disabled={app.status === 'pending'}
                >
                  Pending
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ApplicationDashboard; 